import { Injectable } from "@angular/core";
import { from, iif, mergeMap, Observable, Subject, tap } from "rxjs";
import { TaskStatusService } from "../../../on-premise-sync-progress-dialog/task-status.service";
import { SubjectOnPremiseSyncService } from "./subject-on-premise-sync.service";
import { InspectionTemplateOnPremiseSyncService } from "./inspection-template-on-premise-sync.service";
import { InspectionFirstSyncService } from "./inspection-first-sync.service";
import { InspectionImageFirstSyncService } from "./inspection-image-first-sync.service";
import { ReportTemplateSyncService } from "./report-template-sync.service";
import { InspectionReportSyncService } from "./inspection-report-sync.service";
import { ModifiedImagesFirstSyncService } from "./modified-images-first-sync.service";
import { AssetModuleSyncService } from "../asset-module-sync.service";
import { FeatureFlagService } from "../../feature-flag/feature-flag.service";
import { FEATURE_FLAGS } from "../../../app.constants";
import { AssetModuleClient } from "../../asset-module/asset-module-client.service";
import { OnPremiseSyncDataState } from "./on-premise-sync-state.service";

@Injectable({
  providedIn: "root"
})
export class OnPremiseFirstSyncService {
  private onPremiseFirstSyncSubject = new Subject();
  public onPremiseFirstSyncCompleted$ = this.onPremiseFirstSyncSubject.asObservable();

  constructor(
    private inspectionTemplateSyncService: InspectionTemplateOnPremiseSyncService,
    private subjectSyncService: SubjectOnPremiseSyncService,
    private taskStatusService: TaskStatusService,
    private inspectionSyncService: InspectionFirstSyncService,
    private inspectionImageSyncService: InspectionImageFirstSyncService,
    private modifiedImagesFirstSyncService: ModifiedImagesFirstSyncService,
    private inspectionReportSyncService: InspectionReportSyncService,
    private inspectionReportTemplateSyncService: ReportTemplateSyncService,
    private assetModuleSyncService: AssetModuleSyncService,
    private featureFlagService: FeatureFlagService,
    private assetModuleClient: AssetModuleClient,
    private syncState: OnPremiseSyncDataState
  ) {}

  public syncOfflineData(): Observable<unknown> {
    const isAssetModule = this.featureFlagService.isFlagEnabled(FEATURE_FLAGS.ASSET_MODULE)();
    this.initSyncProgressTasks(isAssetModule);

    return iif(() => isAssetModule, this.syncAssetModuleData(), this.syncSubjectsAndTemplates()).pipe(
      mergeMap(() => this.inspectionSyncService.createInspections()),
      tap(() => this.taskStatusService.updateTaskStatus("onPremiseSyncDialog.tasks.createInspections", true)),
      mergeMap(() => this.inspectionImageSyncService.createInspectionImages()),
      tap(() => this.taskStatusService.updateTaskStatus("onPremiseSyncDialog.tasks.createInspectionImages", true)),
      mergeMap(() => this.modifiedImagesFirstSyncService.syncModifiedImages()),
      tap(() => this.taskStatusService.updateTaskStatus("onPremiseSyncDialog.tasks.createModifiedImages", true)),
      mergeMap(() => this.inspectionReportTemplateSyncService.createReportTemplates()),
      tap(() => this.taskStatusService.updateTaskStatus("onPremiseSyncDialog.tasks.createReportTemplates", true)),
      mergeMap(() => this.inspectionReportSyncService.createReports()),
      tap(() => this.taskStatusService.updateTaskStatus("onPremiseSyncDialog.tasks.createReports", true)),
      mergeMap(() => this.inspectionSyncService.deleteInspections()),
      tap(() => this.onPremiseFirstSyncSubject.next({}))
    );
  }

  private syncAssetModuleData(): Observable<unknown> {
    return from(this.assetModuleSyncService.pushToServer()).pipe(
      mergeMap(() => this.assetModuleClient.getAssetTypes()),
      tap((assetTypes) => assetTypes.items.forEach((at) => this.syncState.updateInspectionTemplate(at.id, at.id))),
      tap(() => this.taskStatusService.updateTaskStatus("onPremiseSyncDialog.tasks.syncAssetModuleData", true))
    );
  }

  private syncSubjectsAndTemplates(): Observable<unknown> {
    return this.inspectionTemplateSyncService.createInspectionTemplates().pipe(
      tap(() => this.taskStatusService.updateTaskStatus("onPremiseSyncDialog.tasks.createInspectionTemplates", true)),
      mergeMap(() => this.subjectSyncService.createSubjects()),
      tap(() => this.taskStatusService.updateTaskStatus("onPremiseSyncDialog.tasks.createSubjects", true))
    );
  }

  private initSyncProgressTasks(isAssetModule: boolean) {
    const tasks = [
      ...(!isAssetModule ? [{ name: "onPremiseSyncDialog.tasks.createInspectionTemplates", isCompleted: false }] : []),
      ...(!isAssetModule ? [{ name: "onPremiseSyncDialog.tasks.createSubjects", isCompleted: false }] : []),
      ...(isAssetModule ? [{ name: "onPremiseSyncDialog.tasks.syncAssetModuleData", isCompleted: false }] : []),
      { name: "onPremiseSyncDialog.tasks.createInspections", isCompleted: false },
      { name: "onPremiseSyncDialog.tasks.createInspectionImages", isCompleted: false },
      { name: "onPremiseSyncDialog.tasks.createModifiedImages", isCompleted: false },
      { name: "onPremiseSyncDialog.tasks.createReports", isCompleted: false },
      { name: "onPremiseSyncDialog.tasks.createReportTemplates", isCompleted: false }
    ];
    this.taskStatusService.initTasks(tasks);
  }
}
